import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import SharedHeader from "@/components/SharedHeader";
import SharedFooter from "@/components/SharedFooter";
import { toast } from "sonner";
import { GraduationCap, Mail, Lock, Building2, ArrowLeft, Loader2 } from "lucide-react";

export default function TeacherLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [schoolCode, setSchoolCode] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !password || !schoolCode) {
      toast.error("Please fill in all fields");
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(`${API}/teacher/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          email: email.toUpperCase().trim(),
          password,
          school_code: schoolCode.toUpperCase().trim()
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        toast.success(`Welcome, ${data.teacher.name}!`);
        navigate("/teacher/dashboard");
      } else {
        // Handle validation errors (array) vs simple error messages
        const errorMsg = Array.isArray(data.detail) 
          ? data.detail[0]?.msg || "Login failed"
          : data.detail || "Login failed";
        toast.error(errorMsg);
      }
    } catch (error) {
      toast.error("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex flex-col">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-100">
        <SharedHeader transparent={true} />
      </header>

      {/* Login Form */}
      <main className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-0 shadow-xl">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
              <GraduationCap className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl">Teacher Login</CardTitle>
            <CardDescription>
              Access your class dashboard and student progress
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="schoolCode">School Code</Label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="schoolCode"
                    type="text"
                    placeholder="Enter school code (e.g., DPS001)"
                    value={schoolCode}
                    onChange={(e) => setSchoolCode(e.target.value.toUpperCase())}
                    className="pl-10"
                    data-testid="school-code-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="teacherId">Teacher ID</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="teacherId"
                    type="text"
                    placeholder="T_XXXXXX"
                    value={email}
                    onChange={(e) => setEmail(e.target.value.toUpperCase())}
                    className="pl-10"
                    data-testid="teacher-id-input"
                  />
                </div>
                <p className="text-xs text-gray-500">Your Teacher ID (e.g., T_131426)</p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10"
                    data-testid="password-input"
                  />
                </div>
              </div>
              
              <Button 
                type="submit" 
                className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
                disabled={loading}
                data-testid="login-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  "Sign In"
                )}
              </Button>
            </form>
            
            <div className="mt-6 text-center text-sm text-gray-500">
              <p>Don't have an account?</p>
              <p className="mt-1">Contact your school administrator to get access.</p>
            </div>
          </CardContent>
        </Card>
      </main>
      
      {/* Footer */}
      <SharedFooter />
    </div>
  );
}
